<script setup lang="ts">
import type { ECharts } from 'echarts'
import type { VueInstance } from '@vueuse/core'

const props = defineProps<{
    title?: string
    loading?: boolean
    id?: string
    parentId?: string
    expanded?: string
    condensed?: string
    dimension: Dimension
    country: Country
    instance?: ECharts
    expandedIfNull?: boolean
}>()

/**
 * Check device type.
 */
const { isLargeDesktop, isTablet } = useDeviceType()

/**
 * Use query params.
 */
const { config } = useParams()

/**
 * Whether the current card is expanded.
 */
const isExpanded = computed(() => {
    return unref(countryCardIsSet) && config.countryCard?.[0] === props.id
})

/**
 * Whether the current card is condensed.
 */
const isCondensed = computed(() => {
    return (
        unref(isTablet) &&
        unref(countryCardIsSet) &&
        config.countryCard?.[0] !== props.id
    )
})

/**
 * Whether the element has an ID.
 */
const hasId = computed(() => {
    return props.id !== undefined
})

/**
 * Whetehr the back button should be displayed.
 */
const showBackButton = computed(() => {
    return unref(hasId) && unref(isExpanded)
})

/**
 * Attributes to pass to the root element.
 */
const attrs = useAttrs()

/**
 * Check whether a country card is set.
 */
const countryCardIsSet = computed(() => {
    return typeof config.countryCard?.[0] === 'string'
})

/**
 * Reactive classes depending on expanded state.
 */
const className = computed(() => {
    if (!unref(hasId) && !props.expandedIfNull) {
        return unref(countryCardIsSet)
            ? props.condensed ?? attrs.class
            : attrs.class
    }

    return unref(isExpanded)
        ? props.expanded
        : unref(countryCardIsSet)
        ? props.condensed
        : attrs.class
})

/**
 * Set the country card ID.
 */
function setCountryCardId(id?: string) {
    config.countryCard = !id
        ? []
        : ([id, ...(config.countryCard ?? []).slice(1)] as string[])
}

/**
 * Card container.
 */
const container = ref<VueInstance>()

/**
 * Watch for changes in the country card ID.
 */
watch(
    () => config.countryCard,
    card => {
        const el = unref(container)?.$el

        if (!el) return

        if (card?.[0] === props.id) {
            el.scrollIntoView({
                behavior: 'smooth'
            })
        } else {
            el.scrollTo({
                top: 0,
                behavior: 'smooth'
            })
        }
    }
)
</script>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<template>
    <CountryCardContainer
        ref="container"
        class="aspect-square overflow-hidden sm:aspect-video xl:aspect-auto xl:h-full"
        :class="className"
        :data-dimension="dimension.name"
        :data-country="country.name"
        :title="title"
    >
        <template #toolbar-prepend>
            <button
                v-if="showBackButton"
                type="button"
                @click="setCountryCardId(parentId)"
            >
                <ArrowLeftIcon class="h-4 w-4" />
            </button>
        </template>

        <template #toolbar-append>
            <ChartToolsImage
                v-if="instance"
                :instance="instance"
                direction="bottom-right"
                :title="title"
            />

            <button v-if="isLargeDesktop && hasId" type="button">
                <ArrowsPointingInIcon
                    v-if="isExpanded"
                    class="h-4 w-4"
                    @click="setCountryCardId(parentId)"
                />
                <ArrowsPointingOutIcon
                    v-else
                    class="h-4 w-4"
                    @click="setCountryCardId(id)"
                />
            </button>
        </template>

        <div
            class="flex flex-1 flex-col"
            :class="[instance ? 'overflow-hidden' : 'overflow-y-auto']"
        >
            <slot
                :expanded="isExpanded"
                :condensed="isCondensed"
                :instance="instance"
                :loading="loading"
            />
        </div>
    </CountryCardContainer>
</template>